import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { AdminLoginChallenge } from "@ticketnjoy/types";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAdminLoginMutation, useAdminVerifyMutation } from "@/features/api";
import { ApiError, showApiError } from "@/lib/api-client";

export function LoginPage() {
  const [challenge, setChallenge] = useState<AdminLoginChallenge | null>(null);

  return (
    <AuthCard
      title="Platform Admin"
      description={
        challenge
          ? `${challenge.maskedEmail} adresine 6 haneli bir kod gönderdik.`
          : "TicketNjoy yönetim paneline giriş yap."
      }
    >
      {challenge ? (
        <CodeStep challenge={challenge} onBack={() => setChallenge(null)} />
      ) : (
        <CredentialsStep onChallenge={setChallenge} />
      )}
    </AuthCard>
  );
}

function CredentialsStep({ onChallenge }: { onChallenge: (c: AdminLoginChallenge) => void }) {
  const login = useAdminLoginMutation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      const res = await login.mutateAsync({ email, password });
      if (res.challenge) onChallenge(res.challenge);
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">E-posta</Label>
        <Input
          id="email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="password">Şifre</Label>
          <Link
            to="/forgot-password"
            className="text-sm text-muted-foreground hover:text-foreground hover:underline"
          >
            Şifremi unuttum
          </Link>
        </div>
        <Input
          id="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <Button type="submit" className="w-full" disabled={login.isPending}>
        {login.isPending ? "Kontrol ediliyor..." : "Devam et"}
      </Button>
    </form>
  );
}

function CodeStep({
  challenge,
  onBack,
}: {
  challenge: AdminLoginChallenge;
  onBack: () => void;
}) {
  const verify = useAdminVerifyMutation();
  const [code, setCode] = useState("");

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await verify.mutateAsync({ challengeToken: challenge.challengeToken, code });
    } catch (err) {
      showApiError(err);
      if (err instanceof ApiError && err.code === "challenge_expired") onBack();
      setCode("");
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="code">Giriş kodu</Label>
        <Input
          id="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          maxLength={6}
          pattern="\d{6}"
          required
          className="text-center font-mono text-lg tracking-[0.5em]"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        />
        <p className="text-xs text-muted-foreground">
          Kod 10 dakika geçerli. Bu IP adresinden 30 gün boyunca tekrar sorulmaz.
        </p>
      </div>
      <Button type="submit" className="w-full" disabled={verify.isPending || code.length !== 6}>
        {verify.isPending ? "Doğrulanıyor..." : "Giriş yap"}
      </Button>
      <Button type="button" variant="ghost" className="w-full" onClick={onBack}>
        Geri dön
      </Button>
    </form>
  );
}

export function AuthCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          <CardTitle className="text-2xl">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </div>
  );
}
